import { cn } from '../../lib/utils';
import type { ReportingSummary } from '../../types/reporting';
import type { TrendGrouping } from './chartDate';
import { ChartStyleToggle } from './ChartStyleToggle';
import { ChartGripHandle } from './ReportingChartGrid';
import type { ChartStyle } from './chartPrefs';
import { deltaColorClass, type HeroCard } from './heroCards';
import { PanelTitle } from './PanelTitle';
import { ReportingMiniChart } from './ReportingMiniChart';
import { buildTrendSeries } from './trendSeries';

// One hero stat: label, the value (single line — currency is global), the
// vs-previous-period delta, a per-metric chart-style toggle, and a taller,
// interactive Kyck-green mini chart of the metric's trend for the selected
// currency + grouping. The whole card doubles as the big-chart metric selector
// (click → plots this metric); the style toggle stops propagation so it doesn't
// select. A div (not <button>) so the interactive mini + style toggle can nest.
export function HeroStatCard({
  card,
  summary,
  grouping,
  style,
  onStyleChange,
  selected,
  onSelect,
}: {
  card: HeroCard;
  summary: ReportingSummary;
  grouping: TrendGrouping;
  style: ChartStyle;
  onStyleChange: (key: string, style: ChartStyle) => void;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const mini = buildTrendSeries(summary, card.key, grouping);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect(card.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(card.key);
        }
      }}
      className={cn(
        'relative cursor-pointer rounded-lg border bg-white p-4 text-left transition-colors',
        'hover:border-kyck-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-kyck-500 ring-1 ring-kyck-500' : 'border-slate-200',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {/* The shared panel-title treatment — see PanelTitle for why a tile's
            label is not secondary text. `lowercase` is Meta's house style for
            metric labels and is unrelated to the weight/contrast. */}
        <PanelTitle selected={selected} className="lowercase" title={card.label}>
          {card.label}
        </PanelTitle>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* The grid's drag handle, hosted here rather than as a strip above
              the tile — see ReportingChartGrid. It stops click (not mousedown),
              so dragging never also selects this metric. */}
          <ChartGripHandle title={`Drag to move ${card.label} · resize from its bottom-right corner`} />
          <ChartStyleToggle value={style} onChange={(s) => onStyleChange(card.key, s)} />
        </div>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-semibold leading-tight text-slate-900">{card.valueLine}</div>
        {card.delta && (
          <span
            className={cn('text-xs font-medium', deltaColorClass(card.tone, card.delta.direction))}
            title="vs the previous equivalent period"
          >
            {card.delta.direction === 'up' ? '▲' : card.delta.direction === 'down' ? '▼' : '–'}{' '}
            {Math.abs(card.delta.pct)}%
          </span>
        )}
      </div>
      {/* Interactive trend for this metric (selected currency + grouping).
          `reporting-chart-panel-grow` is what lets a RESIZED tile grow its
          mini-chart instead of adding blank space — the wrapper has to opt into
          the panel's flex chain explicitly, see the note in index.css. Outside
          the grid the class matches nothing, so this renders exactly as before. */}
      <div className="reporting-chart-panel-grow mt-3">
        <ReportingMiniChart data={mini} style={style} />
      </div>
    </div>
  );
}
